/* oxlint-disable complexity */
import { useRef } from 'react';
import type React from 'react';

import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { SectionSeparator, Separator } from '#src/app/pages/petitionDetails/shared/components';
import {
	AfterDescriptionWaypoint,
	AfterMobileActionButtonWaypoint,
} from '#src/app/pages/petitionDetails/shared/waypoints';
import { MembershipAwarenessSunrise } from '#src/app/shared/components/membershipAwareness';

import { PetitionStarter as PetitionStarterSunrise } from '../components/PetitionStarterSunrise';
import { useIsEndorsementsPetitionEnabled } from '../hooks/isEndorsementsPetitionEnabled';
import { useIsFullSunriseEnabled } from '../hooks/isFullSunriseEnabled';
import { useIsMomentumBarEnabled } from '../hooks/isMomentumBarEnabled';
import { useStickyBehavior } from '../hooks/mainContainer/useStickyBehavior';
import { PublicFieldContextProvider } from '../hooks/publicFieldContext';

import { ContentSummary as ContentSummarySunrise } from './contentSummarySunrise';
import { DecisionMakerSection } from './DecisionMakerSection';
import { DescriptionSunriseContainer } from './descriptionSunrise';
import { EmbeddedSignForm } from './embeddedSignForm';
import { EndorsementsContainer } from './endorsements';
import { HeadingContainerMobile } from './HeadingMobile';
import { HeadingContainerMobile as HeadingContainerMobileSunrise } from './HeadingMobileSunrise';
import { HeadingContainer as HeadingContainerSunrise } from './HeadingSunrise';
import { MediaHitsContainerSunrise } from './MediaHitsContainerSunrise';
import { MobileActionButtonContainer as MobileActionButtonContainerSunrise } from './mobileActionButtonSunrise';
import { MomentumBarExperimentContent } from './momentumBar';
import { PetitionTimestampSunrise } from './PetitionTimestampSunrise';
import {
	RecentSignersViewportNormal as RecentSignersViewportNormalSunrise,
	RecentSignersViewportSmall as RecentSignersViewportSmallSunrise,
} from './recentSignersSunrise';
import { ReportAbuseContainerSunrise as ReportAbuseSunriseContainer } from './ReportAbuseSunrise';
import { SapCtaContainer } from './SapCta';
import { ScrollTrackingWaypointContainer } from './ScrollTrackingWaypoint';
import { SharePetition as SharePetitonSunrise } from './shareSunriseExperiment/SharePetition';
import { SideContainer as SideContainerSunrise } from './sideSunrise';
import { SignatureStatsMobileContainer } from './stats/signatureStatsSunrise';
import { TagBreadcrumbsSunrise } from './TagBreadcrumbsSunrise';
import { TemporaryEndorsementCard } from './TemporaryEndorsementCard';
import { TheSupportersSunrise } from './TheSupportersSunrise';
import { UpdatesContainer as UpdatesSunriseContainer } from './UpdatesSunrise';

// oxlint-disable-next-line max-lines-per-function
export const MainContainer: React.FC = () => {
	const {
		data: {
			petition: { id: petitionId, slug, photo },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { signed },
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const canBeSticky = useStickyBehavior(signatureBlockRef);

	const {
		data: { isSunriseRecentSignersEnabled },
	} = useIsFullSunriseEnabled();

	const isEndorsementsEnabled = useIsEndorsementsPetitionEnabled();

	const {
		data: { petitionPageMomentumBarExperiment },
	} = useIsMomentumBarEnabled();

	const isMomentumBarExperimentEnabled = petitionPageMomentumBarExperiment === 'enabled';

	return (
		<PublicFieldContextProvider>
			<Grid className="grid-rows-[auto] px-0 lg:max-w-[1376px] lg:grid-cols-14! lg:grid-rows-[auto]">
				<div
					className={cn('col-span-full hidden w-full md:row-start-1 md:block', {
						'lg:col-start-2 lg:-col-end-1': photo === null,
					})}
				>
					{photo === null ? null : <HeadingContainerSunrise />}
				</div>

				<div
					className={cn('col-span-full max-md:px-0 md:col-span-5 md:container lg:col-start-2! lg:col-end-11!', {
						'md:row-start-2': photo !== null,
					})}
				>
					{photo === null ? <HeadingContainerMobile /> : <HeadingContainerMobileSunrise />}

					<div className="p-4 md:p-0">
						<div className="hidden md:block">
							{!isMomentumBarExperimentEnabled && isEndorsementsEnabled && <TemporaryEndorsementCard />}

							{isMomentumBarExperimentEnabled && <MomentumBarExperimentContent petitionId={petitionId} />}
							{!isMomentumBarExperimentEnabled && (
								<>
									{isSunriseRecentSignersEnabled && <RecentSignersViewportNormalSunrise />}
									<ContentSummarySunrise />
								</>
							)}
							<DescriptionSunriseContainer />

							<ReportAbuseSunriseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}
							<PetitionStarterSunrise />
						</div>

						<div className="block md:hidden">
							<MobileActionButtonContainerSunrise sticky={false} variant="v1" />

							<AfterMobileActionButtonWaypoint />
							<SignatureStatsMobileContainer />

							{!isMomentumBarExperimentEnabled && isEndorsementsEnabled && <TemporaryEndorsementCard />}

							{!isMomentumBarExperimentEnabled && isSunriseRecentSignersEnabled && (
								<RecentSignersViewportSmallSunrise />
							)}
							{isMomentumBarExperimentEnabled && <MomentumBarExperimentContent petitionId={petitionId} />}
							{!isMomentumBarExperimentEnabled && <ContentSummarySunrise />}
							<DescriptionSunriseContainer />

							<ReportAbuseSunriseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}

							<PetitionStarterSunrise />

							<EmbeddedSignForm />
						</div>

						<ScrollTrackingWaypointContainer name="scroll_past_body" />
						<AfterDescriptionWaypoint />
					</div>
				</div>
				{/* We need to modify the row-span to match the exact last row before the first full width module */}
				<div
					data-qa="second-column-container"
					className="h-0 md:col-start-6 md:col-end-9 md:row-span-3 md:row-start-1 md:mb-20 md:h-full lg:col-start-11 lg:col-end-15"
				>
					<div className="block md:hidden">
						<MobileActionButtonContainerSunrise sticky={true} variant="v1" />
					</div>
					<div
						data-qa="sticky-action-panel-container"
						className={cn(
							'relative top-6 right-0 z-12 mt-0 hidden md:mt-6! md:mr-6! md:block md:max-w-[350px] lg:mt-6! lg:mr-6 lg:max-w-[350px]',
							{
								'!sticky': canBeSticky,
							},
						)}
					>
						<div>
							<SideContainerSunrise containerRef={signatureBlockRef} />
						</div>
					</div>
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SapCtaContainer />

					<DecisionMakerSection />

					<TheSupportersSunrise />
				</div>

				<div className="col-span-full my-6 px-4 md:px-0">
					<MembershipAwarenessSunrise
						sourceLocation="petition_details"
						petitionId={petitionId}
						slug={slug}
						signed={signed}
					/>
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<ScrollTrackingWaypointContainer name="scroll_updates_section" />

					<SectionSeparator />

					<UpdatesSunriseContainer />
				</div>

				<div className="col-span-full mb-20 lg:col-start-1 lg:col-end-15">
					<MediaHitsContainerSunrise />
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SharePetitonSunrise />

					<SectionSeparator />
					<PetitionTimestampSunrise />
					<Separator />
					<TagBreadcrumbsSunrise />
				</div>
			</Grid>
		</PublicFieldContextProvider>
	);
};
